import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Search, Users, IndianRupee, TrendingUp, CheckCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralManagement = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/referrals`);
      setReferrals(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load referrals",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredReferrals = referrals.filter(ref =>
    ref.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ref.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (ref.referrer_name && ref.referrer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (ref.referrer_email && ref.referrer_email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const creditedCount = referrals.filter(r => r.referral_credited).length;
  const pendingCount = referrals.filter(r => !r.referral_credited).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Referral Management</CardTitle>
          <CardDescription>View and manage all referral relationships</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by referrer or referred user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Referrals</p>
                  <p className="text-2xl font-bold text-blue-600">{referrals.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-300" />
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Credited Referrals</p>
                  <p className="text-2xl font-bold text-green-600">{creditedCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pending Credits</p>
                  <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-orange-300" />
              </div>
            </div>
          </div>

          {/* Referrals Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referred User</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Mobile</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referrer</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referrer Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referral Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Joined Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferrals.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                        No referrals found
                      </td>
                    </tr>
                  ) : (
                    filteredReferrals.map((referral) => (
                      <tr key={referral.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium">{referral.name}</td>
                        <td className="px-4 py-3 text-sm">{referral.email}</td>
                        <td className="px-4 py-3 text-sm">{referral.mobile || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">
                          {referral.referrer_name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm">{referral.referrer_email || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-mono">
                            {referral.referrer_code || 'N/A'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {referral.referral_credited ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                              ✓ Credited ₹200
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              Pending Payment
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {referral.created_at ? new Date(referral.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex gap-3">
              <IndianRupee className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 mb-1">Referral Credit Information</p>
                <p className="text-sm text-blue-700">
                  When a referred user completes their first payment, ₹200 is automatically credited to the referrer's account. 
                  The status changes from "Pending Payment" to "Credited" once the payment is complete.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralManagement;
